'use client';
import { useState, useEffect } from 'react';
import { useSmartIntakeCtx, resolvePageCity } from '@/lib/smartIntakeStore';
import { findCity } from '@/lib/israelCities';
import { ctxForPro } from '@/lib/proCityGate';
import { useTypewriter } from '@/lib/useTypewriter';
import { buildWaMessage, buildWaMessageSegments, proFirstName, withHiddenIntro, ctxIsSameCategory } from '@/lib/waMessage';
import { getContacted, setContacted, type ContactedEntry } from '@/lib/contactedState';
import { fireContactIntent } from '@/lib/contactTracking';
import { WA_TYPING_PREVIEW_ENABLED } from '@/lib/featureFlags';
import { isWhatsAppCapable } from '@/lib/phoneValidation';
import { contactViaCompany } from '@/lib/directContact';
import WaIcon from './WaIcon';

const WA_GREEN = 'var(--sk-wa, var(--wa, #12864A))'; // X3D palette on skinned pages; site-wide --wa elsewhere
const WA_GREEN_DARK = 'var(--sk-wa-dark, #0B6B3A)';
const WA_GREEN_MUTED = '#aeca96';

type Props = {
  proName: string;
  proFirstName?: string;
  proPhone?: string | null;
  // 2026-07-30: he_professionals.whatsapp_valid (1/0/undefined) — real onWhatsApp()
  // verification, not a format guess. See lib/phoneValidation.ts.
  whatsappValid?: number | null;
  proId?: number;
  /** he_professionals.gender ('f' → feminine forms in the visitor message, lib/heMsg.mjs leadText) */
  proGender: string | null | undefined;
  /** 2026-09-28 (bughunter #3): on a pro's own page — the message names the visitor's city only if THIS pro serves it */
  // REQUIRED (bughunter re-verify: an omitted prop silently let a city through): pass the pro's area, or null ONLY where the
  // visitor's city comes from this same page's own matching (category/city lists)
  proArea: { lat: number | null; lng: number | null; serviceMode?: string | null; serviceCities?: string | null; serviceRadiusKm?: number | null } | null;
  categorySlug?: string;
  categoryName?: string | null;
  compact?: boolean;
  // Square icon-on-top/label-below button (2026-08-23, CategoryProCard mobile column
  // redesign) — sits side by side with ContactButton's own `square` variant instead of
  // the two stacking full-width rows `compact` produces, so the combined status+buttons
  // column stays within the text column's height budget.
  square?: boolean;
  // Round icon-only floating button (2026-08-23, ProFloatingContact) — pairs with
  // ContactButton's own `floating` variant.
  floating?: boolean;
  highlight?: boolean;
  /** Editable mode: user types directly inside the WA bubble */
  editable?: boolean;
  value?: string;
  onChange?: (v: string) => void;
  /** Slim mode: just the green send button (no bubble/header), custom label */
  slim?: boolean;
  buttonLabel?: string;
  /** ?layout=3.x preview skin (lib/topicLayout.ts): the SAME click handler/routing, a different look. Omitted = unchanged. */
  skin?: { label: string; className?: string; style?: React.CSSProperties; children: React.ReactNode };
};

// WhatsApp-style "typing…" indicator — shown inline in place of a message clause
// that's still waiting on an unanswered questionnaire question.
function TypingDots() {
  return (
    <span className="wa-typing" style={{ display: 'inline-flex', alignItems: 'center', gap: 3, marginInlineStart: 4, verticalAlign: 'middle' }}>
      <span className="wa-typing-dot" />
      <span className="wa-typing-dot" />
      <span className="wa-typing-dot" />
    </span>
  );
}

export default function WaSmartButton({ proName, proFirstName: first, proPhone, whatsappValid, proId, proGender, categorySlug, categoryName, compact = false, square = false, floating = false, highlight = false, editable = false, value, onChange, slim = false, buttonLabel, proArea, skin }: Props) {
  const displayName = proFirstName(proName, first);

  const liveCtx = useSmartIntakeCtx();
  // the city in the message = THE page city (resolvePageCity — same as the badge and the list), then gated per pro
  const pageCity = resolvePageCity(liveCtx);
  const cityCtx = liveCtx && pageCity !== liveCtx.city ? (() => { const f = findCity(pageCity || ''); return { ...liveCtx, city: pageCity, cityLat: f?.lat ?? null, cityLng: f?.lng ?? null }; })() : liveCtx;
  const ctx = editable ? null : ctxForPro(cityCtx, proArea);
  const [mounted, setMounted] = useState(false);
  const [sent, setSent] = useState<ContactedEntry | null>(null);
  const [loading, setLoading] = useState(false);
  const [override, setOverride] = useState<string | null>(null); // user-edited text (pencil)
  const [editingOpen, setEditingOpen] = useState(false);          // textarea open (pencil→save toggle)

  useEffect(() => { setMounted(true); }, []);

  // Was this pro already messaged before (this browser)? Persists the button in
  // its "✅ opened" state across reloads — see lib/contactedState.ts.
  useEffect(() => {
    setSent(getContacted(proId, proPhone));
  }, [proId, proPhone]);

  const builtMessage = buildWaMessage(displayName, mounted ? ctx : null, categoryName);
  // Externally controlled (editable) > user pencil-edit (override) > auto-built.
  // `editingOpen` controls whether the textarea is shown; override persists after saving.
  const isEditing = editable || editingOpen;
  const message = editable ? (value ?? '') : (override ?? builtMessage);

  // Segmented preview: same content as `message`, but a clause still waiting on an
  // unanswered question (e.g. city known, "need" not yet answered) is held back and
  // shown as an animated "typing…" placeholder instead of jumping straight to the
  // full generic text. The actual send-text (`message` above) is unaffected — it's
  // already a complete, valid message the moment city alone is known.
  const segments = buildWaMessageSegments(displayName, mounted ? ctx : null, categoryName);
  const previewText = segments.filter((s): s is { text: string } => 'text' in s).map(s => s.text).join('');
  const hasPendingSegment = segments.some(s => 'pending' in s);
  const displayMessage = editable ? (value ?? '') : (override ?? previewText);

  // Elegant typewriter — smooth ease-out reveal inside the text only
  const { typed, isTyping } = useTypewriter(displayMessage, isEditing);

  if (!proPhone) return null;
  // 2026-07-30 (user-caught follow-up to the professional-import phone-quality
  // fix): a landline/special-service number can NEVER receive WhatsApp, but this
  // button used to build a wa.me link from any phone with zero validation — a
  // visitor landing directly on one of the affected pro pages (not through the
  // questionnaire, which already excludes these numbers via lib/waLinks.ts) could
  // still hit a dead WhatsApp link. Rendering nothing here is safe: every call site
  // (CategoryProCard, pro pages) renders ContactButton (phone call) independently
  // alongside this component, so the visitor still has a working contact option —
  // a landline just can't do WhatsApp, but it can still ring.
  if (!isWhatsAppCapable(proPhone, whatsappValid)) return null;
  const showTypingDots = !isEditing && hasPendingSegment && !isTyping;
  // Bubble shows `message` as-is; the actual wa.me link gets an extra hidden line 2
  // ("פונה אלייך מאתר המומחים, לגבי ...") that never renders in the on-site preview.
  const sendMessage = withHiddenIntro(message, mounted ? ctx : null, categoryName).trim();
  const canSend = message.trim().length > 0;
  // Initial state (no questionnaire answers yet) → show a centered prompt instead of the generic text
  const sameCategoryCtx = ctxIsSameCategory(mounted ? ctx : null, categoryName);
  const hasIntake = mounted && ((sameCategoryCtx && (ctx?.cleanNeed || ctx?.customNeed)) || ctx?.city);
  const isInitial = !editable && override === null && !hasIntake;

  // 2026-08-04: every click (first AND repeat) goes through contactViaCompany() —
  // ALWAYS opens our own company WhatsApp with a tracked /wa/<code> link, never the
  // pro directly. See lib/directContact.ts's top comment for the full incident — an
  // earlier same-day version of this file called openWhatsApp(proPhone, ...) directly
  // (repeat click unconditionally, first click as a fallback), which the user caught
  // live in production as a serious bug: visitors must NEVER be routed straight to a
  // business owner, only ever through our own WhatsApp number.
  async function handleSendClick() {
    if (!proId || loading) return;
    setLoading(true);
    fireContactIntent(proId, proName, categorySlug, 'whatsapp');
    await contactViaCompany({ proId, proGender, displayName, categorySlug, categoryName, intent: 'whatsapp', waMessage: sendMessage, city: ctx?.city ?? null, need: ctx && ctxIsSameCategory(ctx, categoryName) ? (ctx.cleanNeed || null) : null });
    setContacted(proId, proPhone, 'whatsapp');
    setSent({ intent: 'whatsapp', at: Date.now() });
    setLoading(false);
  }

  // ── Floating mode (pro's own page, 2026-08-23) — round icon-only button, shown by
  // ProFloatingContact once the header card (with its own message/call buttons) has
  // scrolled out of view. Same click handler/routing as every other mode — only the
  // shape differs. ──
  if (skin) {
    return <button type="button" onClick={handleSendClick} disabled={loading} aria-label={skin.label} title={skin.label} className={skin.className} style={skin.style}>{skin.children}</button>;
  }

  if (floating) {
    return (
      <button onClick={handleSendClick} disabled={loading} title="הודעת וואטסאפ" aria-label="הודעת וואטסאפ"
        className="flex items-center justify-center rounded-full flex-shrink-0"
        style={{ background: WA_GREEN, border: 'none', cursor: 'pointer', width: 52, height: 52, color: '#fff', boxShadow: '0 4px 16px rgba(0,0,0,.25)' }}>
        <WaIcon size={24} />
      </button>
    );
  }

  // ── Square mode (CategoryProCard mobile column, 2026-08-23) ──
  if (square) {
    return (
      <button onClick={handleSendClick} disabled={loading}
        className="flex flex-col items-center justify-center gap-0.5 rounded-xl flex-shrink-0"
        style={{ background: WA_GREEN, border: 'none', cursor: 'pointer', width: 50, height: 50, color: '#fff' }}>
        <WaIcon size={20} />
        <span style={{ fontSize: 10, fontWeight: 800, color: '#fff', lineHeight: 1 }}>הודעה</span>
      </button>
    );
  }

  // ── Compact mode (sticky bar) ──
  if (compact) {
    return (
      <button onClick={handleSendClick} disabled={loading}
        className="flex items-center gap-1.5 py-2 px-3 rounded-xl text-xs font-bold text-white flex-shrink-0"
        style={{ background: WA_GREEN, border: 'none', cursor: 'pointer', whiteSpace: 'nowrap' }}>
        <WaIcon size={14} /> הודעה
      </button>
    );
  }

  // ── Slim mode (button-only, custom label) — disabled until the questionnaire is filled ──
  if (slim) {
    const locked = isInitial && !sent; // no intake context yet (and not already sent) → low opacity, not clickable
    return (
      <button onClick={() => { if (!locked) handleSendClick(); }} disabled={locked || loading}
        title={locked ? 'השלימו את השאלון כדי לשלוח הודעה מותאמת' : undefined}
        className="flex items-center justify-center gap-2 w-full py-2.5 px-3 rounded-xl text-sm font-bold text-white"
        style={{ background: WA_GREEN, border: 'none', cursor: locked ? 'not-allowed' : 'pointer', opacity: locked ? 0.4 : 1, transition: 'opacity .2s' }}
        onMouseEnter={e => { if (!locked) (e.currentTarget as HTMLElement).style.background = WA_GREEN_DARK; }}
        onMouseLeave={e => { if (!locked) (e.currentTarget as HTMLElement).style.background = WA_GREEN; }}>
        <WaIcon size={16} /> {buttonLabel || `שלח את ההודעה ל${displayName}`}
      </button>
    );
  }

  // ── Full mode ──
  // 2026-07-29 (user request, see lib/featureFlags.ts): the live-typing WA bubble is
  // disabled site-wide — render a plain send button with the same ContactModal flow
  // instead. All the bubble/typewriter code below is preserved behind the flag.
  if (!WA_TYPING_PREVIEW_ENABLED) {
    return (
      <button onClick={handleSendClick} disabled={loading}
        className="flex items-center justify-center gap-2 w-full py-3 px-3 rounded-xl text-[15px] font-bold text-white"
        style={{ background: WA_GREEN, border: 'none', cursor: 'pointer' }}
        onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = WA_GREEN_DARK}
        onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = WA_GREEN}>
        <WaIcon size={17} /> שליחת הודעה ל{displayName}
      </button>
    );
  }
  return (
    <>
      <div dir="rtl" className={highlight ? 'wa-glow' : ''}
        style={{ borderRadius: 14, overflow: 'hidden', boxShadow: '0 4px 18px rgba(0,0,0,.12)' }}>
        {/* Header — centered */}
        <div style={{ background: '#075e54', padding: '9px 14px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>✨ הודעת WhatsApp שהכנת</span>
        </div>

        {/* Bubble — editable textarea or read-only preview (with pencil). Always shows the
            live message preview, typing itself in from the very first render — even before
            any question is answered — rather than a static "complete the questionnaire" box. */}
        <div style={{ background: '#eae6df', padding: '10px 10px 6px', minHeight: 60 }}>
          {isEditing ? (
            <div style={{
              background: '#dcf8c6', borderRadius: '10px 2px 10px 10px',
              padding: '8px 11px', boxShadow: '0 1px 2px rgba(0,0,0,.1)', width: '100%', boxSizing: 'border-box',
              position: 'relative',
            }}>
              {/* Save (checkmark) — only for pencil-edit mode, to exit the textarea */}
              {!editable && (
                <button onClick={() => setEditingOpen(false)} title="שמור את ההודעה"
                  style={{
                    position: 'absolute', top: 5, left: 5, width: 24, height: 24, borderRadius: '50%',
                    border: 'none', background: WA_GREEN, color: '#fff',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
                    boxShadow: '0 1px 3px rgba(0,0,0,.2)',
                  }}>
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 6L9 17l-5-5"/>
                  </svg>
                </button>
              )}
              <textarea
                autoFocus={!editable}
                value={editable ? (value ?? '') : (override ?? '')}
                onChange={e => editable ? onChange?.(e.target.value) : setOverride(e.target.value)}
                rows={5}
                placeholder={`כתוב כאן את ההודעה ל${displayName}...`}
                style={{
                  width: '100%', border: 'none', background: 'transparent', outline: 'none',
                  resize: 'none', fontSize: 13, lineHeight: 1.65, color: '#111',
                  fontFamily: 'inherit', boxSizing: 'border-box',
                  paddingInlineStart: editable ? 0 : 26,
                }}
              />
            </div>
          ) : (
            <div style={{
              background: '#dcf8c6', borderRadius: '10px 2px 10px 10px',
              padding: '8px 30px 20px 11px', fontSize: 13, lineHeight: 1.65, color: '#111',
              whiteSpace: 'pre-wrap', position: 'relative',
              boxShadow: '0 1px 2px rgba(0,0,0,.1)',
              display: 'inline-block', maxWidth: '94%', minWidth: 80,
            }}>
              {/* Edit (overwrite) pencil — top-left corner */}
              <button onClick={() => { setOverride(override ?? builtMessage); setEditingOpen(true); }} title="ערוך את ההודעה"
                style={{
                  position: 'absolute', top: 5, left: 5, width: 22, height: 22, borderRadius: '50%',
                  border: 'none', background: 'rgba(7,94,84,.10)', color: '#075e54',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
                }}
                onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = 'rgba(7,94,84,.20)'}
                onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = 'rgba(7,94,84,.10)'}>
                <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z"/>
                </svg>
              </button>
              {typed}
              {isTyping && <span className="wa-caret" />}
              {showTypingDots && <TypingDots />}
              <span style={{ position: 'absolute', bottom: 4, left: 8, fontSize: 9.5, color: '#667781', display: 'flex', alignItems: 'center', gap: 2 }}>
                עכשיו
                <svg viewBox="0 0 18 11" width="14" height="8" fill="none">
                  <path d="M.5 6l4 4 9-9" stroke="#53bdeb" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M4.5 6l4 4 9-9" stroke="#53bdeb" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
            </div>
          )}
        </div>

        {/* Send button — full-width. Once sent, re-fires directly (no popup). */}
        <button onClick={() => canSend && handleSendClick()} disabled={!canSend}
          className={canSend ? 'wa-send' : ''}
          style={{
            position: 'relative', overflow: 'hidden',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            width: '100%', padding: '14px 0', border: 'none', cursor: canSend ? 'pointer' : 'not-allowed',
            background: canSend ? WA_GREEN : WA_GREEN_MUTED, color: '#fff',
            fontWeight: 800, fontSize: 15, fontFamily: 'inherit',
            borderRadius: '0 0 14px 14px', transition: 'background .15s',
          }}
          onMouseEnter={e => { if (canSend) (e.currentTarget as HTMLElement).style.background = WA_GREEN_DARK; }}
          onMouseLeave={e => { if (canSend) (e.currentTarget as HTMLElement).style.background = WA_GREEN; }}
        >
          <span style={{ position: 'relative', zIndex: 1, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <WaIcon size={18} /> שלח את ההודעה ל{displayName}
          </span>
        </button>

        <style>{`
          .wa-caret {
            display: inline-block; width: 3px; height: 1.05em; border-radius: 2px;
            background: #34b7f1; vertical-align: text-bottom; margin-inline-start: 2px;
            animation: waCaret 1.1s ease-in-out infinite;
          }
          @keyframes waCaret { 0%,100% { opacity: .15 } 50% { opacity: 1 } }
          .wa-typing-dot {
            display: inline-block; width: 5px; height: 5px; border-radius: 50%;
            background: #667781; animation: waTypingBounce 1.2s ease-in-out infinite;
          }
          .wa-typing-dot:nth-child(2) { animation-delay: .15s; }
          .wa-typing-dot:nth-child(3) { animation-delay: .3s; }
          @keyframes waTypingBounce { 0%,60%,100% { transform: translateY(0); opacity: .5; } 30% { transform: translateY(-3px); opacity: 1; } }
          @keyframes waGlow {
            0%   { box-shadow: 0 4px 18px rgba(0,0,0,.12); }
            50%  { box-shadow: 0 8px 30px rgba(7,94,84,.28); }
            100% { box-shadow: 0 4px 18px rgba(0,0,0,.12); }
          }
          .wa-glow { animation: waGlow 2.4s ease-in-out 2; }
          /* Periodic shine sweep across the send button */
          .wa-send::after {
            content: ''; position: absolute; top: 0; bottom: 0; left: -65%;
            width: 45%; pointer-events: none;
            background: linear-gradient(120deg, transparent 0%, rgba(255,255,255,.55) 50%, transparent 100%);
            transform: skewX(-20deg);
            animation: waShine 3.6s ease-in-out infinite;
          }
          @keyframes waShine {
            0% { left: -65%; }
            22% { left: 140%; }
            100% { left: 140%; }
          }
        `}</style>
      </div>
    </>
  );
}
